import { StatusCodes, getReasonPhrase } from "http-status-codes";
import { json } from "react-router-dom";
import { isAfter, isBefore } from "date-fns";
import {
  checkPermission,
  getManagedUsers,
  getUsersClass,
  getDepartureFormByIdResponse,
  checkPermissionForOnboarding,
  getOnboardingFormById,
  getOnboardingFormByLeverOpportunityId,
  getRoleUpdateFormByLeverOpportunityId,
  getDepartedContractorUsersClass,
  checkPermissionForRoleUpdate,
  getRoleUpdateFormById,
  checkPoePermission,
  checkPermissionForProofOfEmploymentUpdate,
  getProofOfEmploymentFormById,
  checkIfFormIsOpen,
  getValidThirdPartyCompanies,
  checkPermissionForParentalLeave,
  checkPermissionForPurchasedLeave,
  getPurchasedLeaveFormById,
  getAllPermanentEmployees,
  getAllManagedPermanentEmployees,
} from "../../apis/kilnBackendApis";
import { formTypeToApiEndpointMapping, formTypeToFormNameMapping, isNilOrEmpty, isNotNilEmpty } from "../../utils";
import UserFactory from "../../factories/UserFactory";

function badRequest(msg) {
  return new Response(msg ?? getReasonPhrase(StatusCodes.BAD_REQUEST), { status: StatusCodes.BAD_REQUEST });
}

function notFound() {
  return new Response(getReasonPhrase(StatusCodes.NOT_FOUND), { status: StatusCodes.NOT_FOUND });
}

function unauthorised() {
  return new Response(getReasonPhrase(StatusCodes.UNAUTHORIZED), { status: StatusCodes.UNAUTHORIZED });
}

// Shared by the loaders whose permission endpoint answers 403 with a message.
function throwIfForbidden(resp, formType) {
  if (resp.status === StatusCodes.FORBIDDEN) {
    throw json({ formType }, { status: StatusCodes.FORBIDDEN, statusText: resp.data?.message });
  }
}

// Simple promise deduplication for permission checks (no persistent caching)
let permissionPromises = new Map();

const FormTypePermissionCheckLoader = async ({ request: { url } }) => {
  const splitPathname = new URL(url)?.pathname?.split("/");
  const formTypeFromPathname = splitPathname.at(splitPathname.indexOf("form") + 1);
  const formTypeForApi = formTypeToApiEndpointMapping[formTypeFromPathname];

  if (!formTypeForApi) {
    throw new Error("Bad Form Type");
  }

  const cacheKey = formTypeForApi;

  // Check for in-flight permission request to prevent concurrent calls
  const existingPromise = permissionPromises.get(cacheKey);
  if (existingPromise) {
    return existingPromise;
  }

  // Create promise and store it to prevent concurrent calls
  const permissionPromise = (async () => {
    try {
      const resp = await checkPermission(formTypeForApi);

      if (resp.status === StatusCodes.NOT_FOUND) {
        throw notFound();
      }
      if (resp.status === StatusCodes.UNAUTHORIZED) {
        throw unauthorised();
      }
      if (resp.status === StatusCodes.FORBIDDEN) {
        throw json(
          {
            formType: formTypeToFormNameMapping[formTypeFromPathname] ?? "Employment",
          },
          { status: StatusCodes.FORBIDDEN },
        );
      }

      if (resp.status === StatusCodes.OK) {
        return new Response();
      } else {
        throw new Error();
      }
    } finally {
      // Clear the in-flight promise after completion
      permissionPromises.delete(cacheKey);
    }
  })();

  permissionPromises.set(cacheKey, permissionPromise);
  return permissionPromise;
};

export const GetAllEmployeesLoader = async ({ request }) => {
  await FormTypePermissionCheckLoader({ request });
  const employees = await getUsersClass();
  if (!employees) {
    throw new Error();
  }

  return { employees };
};

export const GetDepartureFormByIdLoader = async ({ params: { id } }) => {
  const formId = parseInt(id, 10);
  if (Number.isNaN(formId)) {
    throw badRequest("Incorrect format");
  }
  const resp = await getDepartureFormByIdResponse(formId);
  if (resp.status === StatusCodes.NOT_FOUND) {
    throw notFound();
  }
  if (resp.status === StatusCodes.FORBIDDEN) {
    throw json({ formType: "Departure" }, { status: StatusCodes.FORBIDDEN });
  }
  if (resp.status !== StatusCodes.OK) {
    throw new Error(resp?.data?.message);
  }

  return json(resp.data);
};

export const GetOnboardingFormByIdLoader = async ({ params }) => {
  const formId = parseInt(params?.id, 10);
  if (Number.isNaN(formId)) {
    throw badRequest("Incorrect format");
  }

  const permissionResp = await checkPermissionForOnboarding(formId);
  if (permissionResp.status === StatusCodes.FORBIDDEN) {
    throw json({ formType: "Onboarding" }, { status: StatusCodes.FORBIDDEN });
  }
  if (permissionResp.status === StatusCodes.NOT_FOUND) {
    throw notFound();
  }

  const resp = await getOnboardingFormById(formId);
  if (resp.status === StatusCodes.NOT_FOUND) {
    throw notFound();
  }
  if (resp.status !== StatusCodes.OK) {
    throw new Error(resp?.data?.message);
  }

  return json(resp.data);
};

export const GetOnboardingFormByOpportunityIdIdLoader = async (requestUrl) => {
  const url = new URL(requestUrl);
  const opportunityId = url.searchParams.get("opportunityId");

  if (!opportunityId) {
    return { opportunityId: null, data: null };
  }

  const response = await getOnboardingFormByLeverOpportunityId(opportunityId);
  if (response.status !== StatusCodes.OK) {
    throw new Error(response?.data?.message);
  }

  return { opportunityId: opportunityId, data: response.data };
};

// Use in-memory cache instead of sessionStorage to avoid quota issues
let employeesCache = null;
let employeesCacheTime = 0;
let cachePromise = null; // Prevent concurrent API calls
const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes

export const GetAllEnabledEmployeesLoader = async ({ request }) => {
  await FormTypePermissionCheckLoader({ request });

  // Check if we have valid cached data
  const now = Date.now();
  if (employeesCache && now - employeesCacheTime < CACHE_DURATION) {
    return { employees: employeesCache };
  }

  // If there's already a request in progress, wait for it
  if (cachePromise) {
    return cachePromise;
  }

  // Create the promise and cache it to prevent concurrent calls
  cachePromise = (async () => {
    try {
      const employeesClass = await getUsersClass();
      if (!employeesClass) {
        throw new Error();
      }

      const employees = employeesClass.filter((user) => user.isADAccountEnabled);

      // Cache the result in memory
      employeesCache = employees;
      employeesCacheTime = now;

      return { employees };
    } finally {
      // Clear the promise so new requests can be made
      cachePromise = null;
    }
  })();

  return cachePromise;
};

export const GetRoleUpdateFormByOpportunityIdIdLoader = async (requestUrl) => {
  const url = new URL(requestUrl);
  const opportunityId = url.searchParams.get("opportunityId");

  if (!opportunityId) {
    return { opportunityId: null, data: null };
  }

  const response = await getRoleUpdateFormByLeverOpportunityId(opportunityId);
  if (response.status !== StatusCodes.OK) {
    throw new Error(response?.data?.message);
  }

  return { opportunityId: opportunityId, data: response.data };
};

// Cache for role update employees data to prevent repeated API calls
let roleUpdateEmployeesCache = null;
let roleUpdateEmployeesCacheTime = 0;
let roleUpdateEmployeesCachePromise = null; // Prevent concurrent API calls
const ROLE_UPDATE_CACHE_DURATION = 5 * 60 * 1000; // 5 minutes

export const GetRoleUpdateEmployeesLoader = async ({ request }) => {
  await FormTypePermissionCheckLoader({ request });

  // Check if we have valid cached data
  const now = Date.now();
  if (roleUpdateEmployeesCache && now - roleUpdateEmployeesCacheTime < ROLE_UPDATE_CACHE_DURATION) {
    return roleUpdateEmployeesCache;
  }

  // If there's already a request in progress, wait for it
  if (roleUpdateEmployeesCachePromise) {
    return roleUpdateEmployeesCachePromise;
  }

  // Create the promise and cache it to prevent concurrent calls
  roleUpdateEmployeesCachePromise = (async () => {
    try {
      const employeesClass = await getUsersClass();
      if (!Array.isArray(employeesClass)) {
        throw new Error();
      }

      const employees = employeesClass.filter((user) => user.isADAccountEnabled);

      const inactiveAndDepartedUsers = await getDepartedContractorUsersClass();

      if (!Array.isArray(inactiveAndDepartedUsers)) {
        throw new Error();
      }
      if (isNotNilEmpty(employeesClass) && isNotNilEmpty(employees) && employeesClass.length !== employees.length) {
        inactiveAndDepartedUsers.push(...employeesClass.filter((user) => !employees.includes(user)));
      }

      const result = { employees, inactiveAndDepartedUsers };

      // Cache the result in memory
      roleUpdateEmployeesCache = result;
      roleUpdateEmployeesCacheTime = now;

      return result;
    } finally {
      // Clear the promise so new requests can be made
      roleUpdateEmployeesCachePromise = null;
    }
  })();

  return roleUpdateEmployeesCachePromise;
};

export const GetRoleUpdateFormByIdLoader = async ({ params }) => {
  const formId = parseInt(params?.id, 10);
  if (Number.isNaN(formId)) {
    throw badRequest("Incorrect format");
  }

  const permissionResp = await checkPermissionForRoleUpdate(formId);
  if (permissionResp.status === StatusCodes.FORBIDDEN) {
    throw json({ formType: "Role-Update/Transfer" }, { status: StatusCodes.FORBIDDEN });
  }
  if (permissionResp.status === StatusCodes.NOT_FOUND) {
    throw notFound();
  }
  if (permissionResp.status !== StatusCodes.OK) {
    throw new Error();
  }

  const resp = await getRoleUpdateFormById(formId);
  if (resp.status === StatusCodes.NOT_FOUND) {
    throw notFound();
  }
  if (resp.status !== StatusCodes.OK) {
    throw new Error(resp?.data?.message);
  }

  return json(resp.data);
};

export const GetTalentReviewActiveEmployeesLoader = async ({ request }) => {
  await FormTypePermissionCheckLoader({ request });
  const managedUsers = await getManagedUsers();
  if (!Array.isArray(managedUsers)) {
    throw new Error();
  }
  const today = new Date();
  const employees = managedUsers.filter(
    (user) =>
      user?.isADAccountEnabled &&
      user?.employmentType?.toUpperCase() !== "CAS" &&
      isBefore(new Date(user?.startDate ?? "1970-01-01"), today) &&
      !isAfter(today, new Date(user?.plannedTerminationDate || today)),
  );

  if (isNilOrEmpty(employees)) {
    throw json({ formType: "Talent Review" }, { status: StatusCodes.IM_A_TEAPOT });
  }

  return { employees };
};

export const GetProofOfEmploymentEmployeesLoader = async () => {
  const res = await checkPoePermission();
  if (res.status === StatusCodes.FORBIDDEN) {
    throw json({ formType: "Proof of Employment" }, { status: res.status });
  }

  const hasFullAccess = res?.data?.hasFullAccess;
  const currentUser = res?.data?.user ? UserFactory.create(res?.data?.user) : undefined;

  let employeesClass;
  let employees = [];
  const wholeEmployeeList = await getUsersClass();
  //Authorised
  if (hasFullAccess) {
    //Full access - dropdown will display all users
    employeesClass = wholeEmployeeList;
  } else {
    //Normal access - dropdown will display user (if not COA) and all direct reports (if any)
    employeesClass = (await getManagedUsers()).map((u) => {
      return {
        ...u,
        manager: currentUser,
      };
    });
  }

  if (!Array.isArray(employeesClass)) {
    throw new Error();
  }

  //Remove inactive users and remove all COAs
  if (employeesClass.length > 0) {
    employees = employeesClass.filter((user) => user?.isADAccountEnabled && user?.employmentType !== "COA");
  }
  const isCurrentUserValidEmployee = wholeEmployeeList.some((employee) => employee.id === currentUser.id);
  if (isCurrentUserValidEmployee) {
    employees.push(currentUser);
  }

  return { employees, currentUser, isCurrentUserValidEmployee };
};

export const GetParentalLeaveEmployeesLoader = async () => {
  const resp = await checkPermissionForParentalLeave();
  throwIfForbidden(resp, "Parental Leave");
  const hasFullAccess = resp.data?.hasFullAccess;

  if (resp.status === StatusCodes.OK) {
    let employees = [];
    if (hasFullAccess) {
      employees = await getAllPermanentEmployees();
    } else {
      employees = await getAllManagedPermanentEmployees();
    }
    return { employees };
  }
  throw new Error();
};

// Permanent full-time/part-time only (secondments included), so ineligible employment types are never
// offered, like Parental Leave; the eligibility card remains the backstop. Px see every permanent
// employee; everyone else sees only themselves (managers cannot submit for direct reports).
export const GetPurchasedLeaveAuthorityLoader = async () => {
  const resp = await checkPermissionForPurchasedLeave();
  throwIfForbidden(resp, "Purchased Leave");
  if (resp.status === StatusCodes.OK) {
    const canAccess = resp.data?.windowOpen === true || resp.data?.pxWindowOpen === true;
    if (!canAccess) {
      throw json({ formType: "Purchased Leave - Form Closed" }, { status: StatusCodes.IM_A_TEAPOT });
    }
    const hasFullAccess = resp.data?.hasFullAccess === true;
    const currentUser = resp.data?.user ? UserFactory.create(resp.data.user) : undefined;

    const candidates = hasFullAccess ? await getAllPermanentEmployees() : [currentUser];
    if (!Array.isArray(candidates)) {
      throw new Error();
    }
    // Same employment types the backend eligibility rule accepts (see purchasedLeaveEligibility.ts).
    const purchasableEmploymentTypes = ["FT", "PT", "SEC - FT", "SEC - PT"];
    const employees = candidates.filter(
      (user) => user && purchasableEmploymentTypes.includes(user.employmentType) && user.isADAccountEnabled !== false,
    );

    // One open application per employee. Limited-access users can only submit for themselves, so an open
    // application blocks them up front; Px can still submit for others.
    const canOnlySubmitForSelf = !hasFullAccess;
    if (resp.data?.hasOpenApplication === true && canOnlySubmitForSelf) {
      throw json({ formType: "Purchased Leave - Already Applied" }, { status: StatusCodes.IM_A_TEAPOT });
    }
    return { employees, currentUser };
  }
  throw new Error();
};

export const GetPurchasedLeaveFormByIdLoader = async ({ params }) => {
  const formId = parseInt(params?.id, 10);
  if (Number.isNaN(formId)) {
    throw badRequest("Incorrect format");
  }
  const resp = await getPurchasedLeaveFormById(formId);
  if (resp.status === StatusCodes.FORBIDDEN) {
    throw json({ formType: "Purchased Leave" }, { status: StatusCodes.FORBIDDEN });
  }
  if (resp.status === StatusCodes.NOT_FOUND) {
    throw notFound();
  }
  if (resp.status !== StatusCodes.OK) {
    throw new Error(resp?.data?.message);
  }
  return json(resp.data);
};

export const GetProofOfEmploymentByIdLoader = async ({ params }) => {
  const formId = parseInt(params?.id, 10);
  if (Number.isNaN(formId)) {
    throw badRequest("Incorrect format");
  }

  const permissionResp = await checkPermissionForProofOfEmploymentUpdate(formId);
  if (permissionResp.status === StatusCodes.NOT_FOUND) {
    throw notFound();
  }
  if (permissionResp.status === StatusCodes.FORBIDDEN) {
    throw json({ formType: "Proof of Employment" }, { status: StatusCodes.FORBIDDEN });
  }

  const resp = await getProofOfEmploymentFormById(formId);

  if (resp.status !== StatusCodes.OK) {
    throw new Error(resp?.data?.message);
  }

  return json(resp.data);
};

export const GetEoYEmployeesLoader = async ({ request }) => {
  await FormTypePermissionCheckLoader({ request });
  const resp = await checkIfFormIsOpen();
  if (resp.status !== StatusCodes.OK) {
    throw json({ formType: "End of Year - Form Closed" }, { status: StatusCodes.IM_A_TEAPOT });
  }

  const employeesList = await getManagedUsers();

  if (isNilOrEmpty(employeesList)) {
    throw json({ formType: "End of Year - No Direct Reports" }, { status: StatusCodes.IM_A_TEAPOT });
  }

  const today = new Date();
  const employees = employeesList.filter(
    (user) =>
      user?.isADAccountEnabled &&
      user?.employmentType?.toUpperCase() !== "CAS" &&
      isBefore(new Date(user?.startDate ?? "1970-01-01"), today) &&
      !isAfter(today, new Date(user?.plannedTerminationDate ?? today)),
  );

  return { employees };
};

export const GetThirdPartyLoader = async ({ request }) => {
  await FormTypePermissionCheckLoader({ request });
  const [companyList, employeesClass] = await Promise.all([getValidThirdPartyCompanies(), getUsersClass()]);
  if (!employeesClass || !companyList) {
    throw new Error();
  }
  const employees = employeesClass.filter((user) => user.isADAccountEnabled);

  const companies = companyList.data.map((company) => {
    const updatedCompany = { ...company };
    updatedCompany.taxNumber = company.abn || company.nzbn;
    return updatedCompany;
  });
  return { companies, employees };
};
